import React, { useState, useEffect, useMemo } from 'react';
import { AlertCircle, Activity, MessageCircle, Trophy, Calendar, DollarSign, Users, MessageSquareReply, ThumbsUp } from 'lucide-react';
import { useWhatsAppAgentLeads, useWhatsAppMessageLogs, useMeetingsBooked } from '../../hooks/useSupabaseLeads';
import { usePipelineDeals } from '../../hooks/usePipelineDeals';
import AgentHeader from '../CallAgent/AgentHeader';
import AgentActionPanel from '../CallAgent/AgentActionPanel';
import TodayHighlights from '../CallAgent/TodayHighlights';
import ActivityFeed from '../CallAgent/ActivityFeed';
import PerformanceIntelligence from '../shared/PerformanceIntelligence';
import AgentMilestones from '../shared/AgentMilestones';
import type { MilestoneTier } from '../shared/AgentMilestones';
import NextActionsPanel from '../CallAgent/NextActionsPanel';
import TestAgentModal from '../CallAgent/TestAgentModal';
import WhatsAppHowIWorkModal from './WhatsAppHowIWorkModal';
import FeedbackPopup from '../FeedbackPopup';
import LoadingScreen from '../shared/LoadingScreen';
import { getAgentSettingsRow } from '../../services/settingsService';
import { useTasksQueue } from '../../hooks/useTasksQueue';

const WhatsAppAgentPage = () => {
  const { data: allWhatsAppLeads, isLoading, error } = useWhatsAppAgentLeads();
  const taskQueue = useTasksQueue('whatsappagent');
  const { data: pipelineDeals } = usePipelineDeals();
  const { data: waMessageLogs } = useWhatsAppMessageLogs();
  const { data: allMeetings } = useMeetingsBooked();
  const [showTestAgentModal, setShowTestAgentModal] = useState(false);
  const [showHowIWorkModal, setShowHowIWorkModal] = useState(false);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    getAgentSettingsRow('whatsapp').then(row => {
      if (row?.avatar_url) setAgentAvatarUrl(row.avatar_url);
    });
  }, []);

  const allWhatsAppLogs = useMemo(() => {
    return [...allWhatsAppLeads].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [allWhatsAppLeads]);

  const todayStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const todayEnd = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  }, []);

  const todayWhatsAppLogs = useMemo(() => {
    return allWhatsAppLogs.filter(log => {
      const logDate = new Date(log.created_at);
      return logDate >= todayStart && logDate <= todayEnd;
    });
  }, [allWhatsAppLogs, todayStart, todayEnd]);

  const recentWhatsAppLogs = useMemo(() => {
    return allWhatsAppLogs.slice(0, 50);
  }, [allWhatsAppLogs]);

  const todayWaLogs = useMemo(() => {
    return waMessageLogs.filter(log => {
      const d = new Date(log.created_at);
      return d >= todayStart && d <= todayEnd;
    });
  }, [waMessageLogs, todayStart, todayEnd]);

  const todayStats = useMemo(() => {
    const sentLogs = todayWaLogs.filter(l => l.from_role === 'user');
    const leadsContacted = new Set(sentLogs.map(l => l.lead_id).filter(Boolean)).size;
    const messagesSent = sentLogs.length;
    const repliesReceived = todayWaLogs.filter(l => l.from_role === 'lead').length;

    const repliedLeadIds = new Set(todayWaLogs.filter(l => l.from_role === 'lead').map(l => l.lead_id).filter(Boolean));
    const positiveConversations = allWhatsAppLeads.filter(lead => {
      if (!repliedLeadIds.has(lead.lead_id)) return false;
      const temp = (lead.wa_lead_temp || '').toLowerCase();
      return temp === 'warm' || temp === 'hot';
    }).length;

    const todayMeetings = allMeetings.filter(m => {
      const d = new Date(m.created_at);
      const channel = (m.booked_channel || '').toLowerCase();
      return d >= todayStart && d <= todayEnd && channel.includes('whatsapp');
    }).length;

    return { leadsContacted, messagesSent, repliesReceived, positiveConversations, meetingsBooked: todayMeetings };
  }, [todayWaLogs, allWhatsAppLeads, allMeetings, todayStart, todayEnd]);

  const agentStatus = useMemo(() => {
    if (allWhatsAppLogs.length === 0) return 'idle';

    const lastMessage = allWhatsAppLogs[0];
    const lastMessageTime = new Date(lastMessage.created_at);
    const now = new Date();
    const hoursSinceLastMessage = (now.getTime() - lastMessageTime.getTime()) / (1000 * 60 * 60);

    if (hoursSinceLastMessage < 1) return 'active';
    if (allWhatsAppLeads.some(lead => lead.wa_next_fu_datetime && new Date(lead.wa_next_fu_datetime) > now)) return 'idle';
    return 'processing';
  }, [allWhatsAppLogs, allWhatsAppLeads]);

  const meetingsBooked = useMemo(() => {
    return allMeetings.filter(m => {
      const channel = (m.booked_channel || '').toLowerCase();
      return channel.includes('whatsapp');
    }).length;
  }, [allMeetings]);

  const pipelineValue = useMemo(() => {
    return pipelineDeals
      .filter(d => (d.channel_booked || '').toLowerCase().includes('whatsapp'))
      .reduce((sum, d) => sum + (d.deal_value || 0), 0);
  }, [pipelineDeals]);

  const milestoneTiers: MilestoneTier[] = useMemo(() => [
    {
      icon: Trophy,
      title: '1,000 Messages Sent',
      target: 1000,
      reward: 'Get 200 Free Message Credits',
      currentValue: waMessageLogs.filter(l => l.from_role === 'user').length,
      formatValue: (c, t) => `${c.toLocaleString()} / ${t.toLocaleString()} messages`,
    },
    {
      icon: Calendar,
      title: '10 Meetings Booked',
      target: 10,
      reward: '500 Free Message Credits',
      currentValue: meetingsBooked,
      formatValue: (c, t) => `${c} / ${t} meetings booked`,
    },
    {
      icon: DollarSign,
      title: '$150,000 Pipeline Generated',
      target: 150000,
      reward: '1,000 Free Message Credits',
      currentValue: pipelineValue,
      formatValue: (c, t) => `$${c.toLocaleString()} / $${t.toLocaleString()}`,
    },
  ], [waMessageLogs, meetingsBooked, pipelineValue]);


  if (isLoading && allWhatsAppLeads.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <AgentHeader
        status={agentStatus}
        title="WhatsApp Agent"
        subtitle="I message prospects on WhatsApp, reply and convert chats into meetings."
        icon="whatsapp"
        avatarSrc={agentAvatarUrl}
        avatarAlt="WhatsApp Agent"
        activeLabel="Active Now — Messaging leads"
        processingMessages={[
          'Preparing summaries…',
          'Analyzing conversation tone…',
          'Detecting reply patterns…',
          'Crafting personalized messages…',
          'Scheduling follow-ups…',
          'Checking unread replies…'
        ]}
      />

      <AgentActionPanel
        onTestCall={() => setShowTestAgentModal(true)}
        onHowIWork={() => setShowHowIWorkModal(true)}
        campaignPath="/campaigns/whatsapp"
        settingsPath="whatsapp-agent"
        howIWorkDescription="How I manage WhatsApp conversations and follow-ups."
        viewCampaignDescription="See the WhatsApp touchpoints and campaign performance."
        testMeDescription="See me in action - I'll WhatsApp you now."
        testMeIcon={MessageCircle}
        customizeMeDescription="Adjust my persona, messaging style, and conversation rules."
      />

      <div className="mt-[60px]">
        <TodayHighlights
          metrics={[
            { value: todayStats.leadsContacted, label: 'Leads Contacted', icon: Users },
            { value: todayStats.messagesSent, label: 'Messages Sent', icon: MessageCircle },
            { value: todayStats.repliesReceived, label: 'Replies Received', icon: MessageSquareReply },
            { value: todayStats.positiveConversations, label: 'Positive Conversations', icon: ThumbsUp },
            { value: todayStats.meetingsBooked, label: 'Meetings Booked', icon: Calendar },
          ]}
        />
      </div>

      <div className="space-y-3 mt-[60px]">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
            <Activity className="text-white" size={22} />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Live Operations</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ minHeight: '600px' }}>
          <ActivityFeed
            processingTasks={taskQueue.processingTasks}
            recentFinishedTasks={taskQueue.recentFinishedTasks}
            hasProcessing={taskQueue.hasProcessing}
            hasAnyTasks={taskQueue.hasAnyTasks}
            isLoading={taskQueue.isLoading}
            emptyState={{ icon: MessageCircle, title: 'No messages in queue', subtitle: 'Assign leads to start messaging' }}
          />
          <NextActionsPanel
            pendingTasks={taskQueue.pendingTasks}
            hasAnyTasks={taskQueue.hasAnyTasks}
            isLoading={taskQueue.isLoading}
            emptyState={{ icon: MessageCircle, title: 'No messages scheduled', subtitle: 'Follow-ups and new messages will appear here' }}
          />
        </div>
      </div>

      <div className="mt-[60px]">
        <PerformanceIntelligence agentType="whatsapp" />
      </div>

      <div className="mt-[60px]">
        <AgentMilestones tiers={milestoneTiers} />
      </div>

      <TestAgentModal
        isOpen={showTestAgentModal}
        onClose={() => setShowTestAgentModal(false)}
        agentType="whatsapp"
      />

      <WhatsAppHowIWorkModal
        isOpen={showHowIWorkModal}
        onClose={() => setShowHowIWorkModal(false)}
      />

      <FeedbackPopup isVisible={true} />
    </div>
  );
};

export default WhatsAppAgentPage;
